/**
 * Scroll Reveal Controller for World of Silver
 * Provides smooth, elegant, staggered scroll animations for sections and cards.
 */

(function () {
  'use strict';

  // Check if reduced motion is requested
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    return;
  }

  // Known container selectors whose children should reveal one by one (staggered)
  const GROUP_CONTAINER_SELECTORS = [
    '.explore-collections__grid',
    '.product-carousel__track',
    '.collection-cards-carousel__track',
    '.triple-collection-banner__grid',
    '.dual-collection-banner__grid',
    '.category-grid__track',
    '.skewed-carousel__track',
    '.product-grid',
    '.main-collection-grid .grid',
    '.featured-blog-posts__grid',
    '.about-collection-grid__grid',
    '.footer__blocks',
    '[data-reveal-group]'
  ];

  // Known standalone element selectors that reveal as a cohesive unit
  const STANDALONE_SELECTORS = [
    '.explore-collections__header',
    '.product-carousel__header',
    '.collection-cards-carousel__header-column',
    '.triple-collection-banner__header',
    '.dual-collection-banner__header',
    '.category-grid__heading',
    '.section-header',
    '.about-hero__content',
    '.about-story-split__col',
    '.about-legacy__col',
    '.about-legacy__header',
    '.featured-blog-posts__header',
    '.footer__newsletter',
    '.footer__bottom',
    '[data-reveal]'
  ];

  let observer = null;
  const observedElements = new WeakSet();

  /**
   * Initializes the IntersectionObserver for scroll reveals
   */
  function createObserver() {
    if (observer) return observer;

    // Detect if page-wrapper is scroll root on desktop
    const pageWrapper = document.querySelector('.page-wrapper');
    const isPageWrapperScrollable =
      pageWrapper && window.innerWidth >= 990 && getComputedStyle(pageWrapper).overflowY === 'auto';
    const rootTarget = isPageWrapperScrollable ? pageWrapper : null;

    const options = {
      root: rootTarget,
      rootMargin: '0px 0px -85px 0px', // Triggers when element is visibly 85px inside viewport so animation is clearly seen
      threshold: 0.1
    };

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          revealElement(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, options);

    return observer;
  }

  /**
   * Triggers the reveal animation on a given element
   * @param {HTMLElement} element
   * @param {boolean} [immediate=false]
   */
  function revealElement(element, immediate = false) {
    if (element.classList.contains('is-revealed')) return;

    if (immediate) {
      element.classList.add('is-revealed', 'reveal-complete');
      return;
    }

    element.classList.add('is-revealed');

    // Clean up will-change and transform after transition completes
    const handleTransitionEnd = (e) => {
      if (e.target === element && (e.propertyName === 'transform' || e.propertyName === 'opacity')) {
        element.removeEventListener('transitionend', handleTransitionEnd);
        element.classList.add('reveal-complete');
      }
    };

    element.addEventListener('transitionend', handleTransitionEnd);

    // Fallback timer to ensure reveal-complete is applied even if transitionend is skipped
    setTimeout(() => {
      element.classList.add('reveal-complete');
      element.removeEventListener('transitionend', handleTransitionEnd);
    }, 1800);
  }

  /**
   * Scans the DOM and registers group containers and standalone elements
   * @param {HTMLElement|Document} [context=document]
   */
  function scanAndRegister(context = document) {
    const obs = createObserver();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    // 1. Process Group Containers (stagger children)
    GROUP_CONTAINER_SELECTORS.forEach((groupSelector) => {
      const containers = context.querySelectorAll(groupSelector);
      containers.forEach((container) => {
        container.setAttribute('data-reveal-group', 'true');
        const children = Array.from(container.children).filter(
          (child) => child.nodeType === Node.ELEMENT_NODE && !child.hasAttribute('aria-hidden')
        );

        children.forEach((child, index) => {
          // Cap stagger index at 6 so very large lists don't delay excessively
          const staggerIndex = index % 6;
          child.style.setProperty('--reveal-index', staggerIndex.toString());

          if (!observedElements.has(child)) {
            observedElements.add(child);

            // Only immediately reveal elements strictly in the upper fold on first load
            const rect = child.getBoundingClientRect();
            if (rect.top < windowHeight * 0.65 && rect.bottom > 0) {
              setTimeout(() => {
                revealElement(child);
              }, staggerIndex * 60);
            } else {
              obs.observe(child);
            }
          }
        });
      });
    });

    // 2. Process Standalone Elements
    STANDALONE_SELECTORS.forEach((selector) => {
      const elements = context.querySelectorAll(selector);
      elements.forEach((element) => {
        // Skip if this element is already a child of a group container
        if (element.closest('[data-reveal-group]') && element.closest('[data-reveal-group]') !== element) {
          return;
        }

        element.setAttribute('data-reveal', 'true');

        if (!observedElements.has(element)) {
          observedElements.add(element);

          const rect = element.getBoundingClientRect();
          if (rect.top < windowHeight * 0.65 && rect.bottom > 0) {
            revealElement(element, true);
          } else {
            obs.observe(element);
          }
        }
      });
    });
  }

  /**
   * Initializes the Scroll Reveal system
   */
  function init() {
    // Enable CSS styling hook
    document.documentElement.classList.add('js-scroll-reveal');

    // Initial DOM scan
    scanAndRegister(document);

    // Watch for dynamic DOM additions (e.g. infinite scroll, filter changes, Quick Add)
    const mutationObserver = new MutationObserver((mutations) => {
      let shouldScan = false;
      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0) {
          shouldScan = true;
          break;
        }
      }
      if (shouldScan) {
        window.requestAnimationFrame(() => {
          scanAndRegister(document);
        });
      }
    });

    const targetNode = document.getElementById('MainContent') || document.body;
    if (targetNode) {
      mutationObserver.observe(targetNode, {
        childList: true,
        subtree: true
      });
    }

    // Support Shopify Theme Customizer lifecycle events
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target) {
        scanAndRegister(event.target);
      }
    });

    document.addEventListener('shopify:section:select', (event) => {
      if (event.target) {
        const allReveals = event.target.querySelectorAll('[data-reveal], [data-reveal-group] > *');
        allReveals.forEach((el) => revealElement(el, true));
      }
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
